import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  ChevronRight, ChevronLeft, Check, User, Target, Dumbbell, 
  Apple, Calendar, Trophy, Heart, Sparkles, Home, Users,
  Clock, Zap, Moon, GraduationCap, Instagram, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Onboarding steps - dynamically adjusted based on athlete level
const BASE_STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles },
  { id: 'level', title: 'Athlete Level', icon: GraduationCap },
  { id: 'athlete', title: 'Athlete Info', icon: User },
  { id: 'sport', title: 'Sport & Goals', icon: Target },
  { id: 'equipment', title: 'Equipment', icon: Dumbbell },
  { id: 'schedule', title: 'Schedule', icon: Calendar },
  { id: 'nutrition', title: 'Nutrition', icon: Apple },
  { id: 'household', title: 'Household', icon: Home },
  { id: 'complete', title: 'Complete', icon: Trophy },
];

// College-specific steps (replaces some base steps)
const COLLEGE_STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Sparkles },
  { id: 'level', title: 'Athlete Level', icon: GraduationCap },
  { id: 'college_info', title: 'College Info', icon: GraduationCap },
  { id: 'sport', title: 'Sport & Goals', icon: Target },
  { id: 'equipment', title: 'Equipment', icon: Dumbbell },
  { id: 'schedule', title: 'Schedule', icon: Calendar },
  { id: 'nutrition', title: 'Nutrition', icon: Apple },
  { id: 'nil', title: 'NIL Status', icon: Star },
  { id: 'complete', title: 'Complete', icon: Trophy },
];

// College/University list
const COLLEGES = [
  { id: 'alabama', name: 'University of Alabama', conference: 'SEC' },
  { id: 'ohio_state', name: 'Ohio State University', conference: 'Big Ten' },
  { id: 'georgia', name: 'University of Georgia', conference: 'SEC' },
  { id: 'michigan', name: 'University of Michigan', conference: 'Big Ten' },
  { id: 'clemson', name: 'Clemson University', conference: 'ACC' },
  { id: 'texas', name: 'University of Texas', conference: 'SEC' },
  { id: 'usc', name: 'USC', conference: 'Big Ten' },
  { id: 'notre_dame', name: 'Notre Dame', conference: 'Independent' },
  { id: 'lsu', name: 'Louisiana State University', conference: 'SEC' },
  { id: 'oklahoma', name: 'University of Oklahoma', conference: 'SEC' },
  { id: 'penn_state', name: 'Penn State University', conference: 'Big Ten' },
  { id: 'florida', name: 'University of Florida', conference: 'SEC' },
  { id: 'oregon', name: 'University of Oregon', conference: 'Big Ten' },
  { id: 'tennessee', name: 'University of Tennessee', conference: 'SEC' },
  { id: 'miami', name: 'University of Miami', conference: 'ACC' },
  { id: 'duke', name: 'Duke University', conference: 'ACC' },
  { id: 'stanford', name: 'Stanford University', conference: 'ACC' },
  { id: 'ucla', name: 'UCLA', conference: 'Big Ten' },
  { id: 'other', name: 'Other College/University', conference: '' },
];

// Sport options
const SPORTS = [
  { id: 'football', name: 'Football', positions: ['QB', 'RB', 'WR', 'TE', 'OL', 'DL', 'LB', 'CB', 'S', 'K/P'] },
  { id: 'basketball', name: 'Basketball', positions: ['PG', 'SG', 'SF', 'PF', 'C'] },
  { id: 'baseball', name: 'Baseball', positions: ['P', 'C', 'IF', 'OF'] },
  { id: 'soccer', name: 'Soccer', positions: ['GK', 'DEF', 'MID', 'FWD'] },
  { id: 'track', name: 'Track & Field', positions: ['Sprints', 'Distance', 'Jumps', 'Throws'] },
  { id: 'swimming', name: 'Swimming', positions: ['Sprint', 'Distance', 'IM'] },
  { id: 'volleyball', name: 'Volleyball', positions: ['Setter', 'Outside', 'Middle', 'Libero'] },
  { id: 'tennis', name: 'Tennis', positions: ['Singles', 'Doubles'] },
  { id: 'golf', name: 'Golf', positions: [] },
  { id: 'general', name: 'Multi-Sport / General', positions: [] },
];

// Equipment tiers based on document
const EQUIPMENT_TIERS = {
  speed_agility: {
    name: 'Speed & Agility',
    icon: Zap,
    items: {
      starter: [
        { id: 'cones', name: 'Agility Cones (20-30)', alt: 'Shoes, water bottles, chalk marks' },
        { id: 'jump_rope', name: 'Jump Rope', alt: 'Invisible jump rope motion' },
        { id: 'ladder', name: 'Agility Ladder', alt: 'Tape lines or chalk' },
        { id: 'mini_hurdles', name: 'Mini Hurdles (6 set)', alt: 'Rolled towels, books, sticks' },
      ],
      intermediate: [
        { id: 'speed_chute', name: 'Speed Parachute', alt: null },
        { id: 'resistance_sled', name: 'Resistance Sled', alt: null },
      ],
      elite: [
        { id: 'timing_system', name: 'Timing System', alt: null },
        { id: 'reaction_lights', name: 'Reaction Lights', alt: null },
      ]
    }
  },
  strength: {
    name: 'Strength & Conditioning',
    icon: Dumbbell,
    items: {
      starter: [
        { id: 'resistance_bands', name: 'Resistance Bands (Light & Medium)', alt: 'Partner resistance, towel pulls' },
        { id: 'pull_up_bar', name: 'Pull-Up Bar', alt: 'Tree branch, playground' },
      ],
      intermediate: [
        { id: 'weighted_vest', name: 'Weighted Vest (10-20 lbs)', alt: null },
        { id: 'medicine_ball', name: 'Medicine Ball (6-12 lbs)', alt: 'Basketball, weighted backpack' },
        { id: 'kettlebell', name: 'Kettlebell (15-25 lbs)', alt: null },
      ],
      elite: [
        { id: 'barbell_set', name: 'Barbell & Weight Set', alt: null },
        { id: 'squat_rack', name: 'Squat Rack', alt: null },
        { id: 'bench', name: 'Adjustable Bench', alt: null },
      ]
    }
  },
  recovery: {
    name: 'Recovery & Mobility',
    icon: Moon,
    items: {
      starter: [
        { id: 'foam_roller', name: 'Foam Roller', alt: 'Tennis ball, rolling pin' },
        { id: 'yoga_mat', name: 'Yoga Mat', alt: 'Carpet, towel' },
      ],
      intermediate: [
        { id: 'massage_gun', name: 'Massage Gun', alt: null },
        { id: 'ice_packs', name: 'Ice Packs / Cold Compress', alt: 'Frozen peas' },
        { id: 'epsom_salt', name: 'Epsom Salt (for baths)', alt: null },
      ],
      elite: [
        { id: 'compression_boots', name: 'Compression Boots', alt: null },
        { id: 'cold_plunge', name: 'Cold Plunge / Ice Bath', alt: null },
      ]
    }
  }
};

// Season phases
const SEASON_PHASES = [
  { id: 'offseason', name: 'Off-Season', desc: 'Building phase - focus on strength & skill development' },
  { id: 'preseason', name: 'Pre-Season', desc: 'Ramping up - sport-specific conditioning' },
  { id: 'inseason', name: 'In-Season', desc: 'Maintenance mode - recovery is priority' },
  { id: 'postseason', name: 'Post-Season / Transition', desc: 'Active recovery & mental reset' },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    // Athlete Level (NEW)
    athleteLevel: '', // high_school, college, pro
    
    // Athlete Info
    athleteName: '',
    athleteAge: '',
    athleteGrade: '',
    parentName: '',
    parentRole: 'parent', // parent, coach, guardian
    
    // College-specific info (NEW)
    currentSchool: '',
    customSchoolName: '',
    collegeYear: '', // freshman, sophomore, junior, senior, 5th_year, grad
    eligibilityYears: 4,
    scholarshipType: '', // full, partial, walk_on
    conferenceLevel: '', // D1, D2, D3, NAIA, JUCO
    
    // NIL Status - college only (NEW)
    hasNilDeals: false,
    nilEarningsRange: '', // none, under_10k, 10k_50k, 50k_100k, over_100k
    nilInterests: [], // social_media, endorsements, appearances, camps, merchandise
    agentStatus: '', // none, seeking, have_agent
    
    // Sport & Goals
    primarySport: '',
    position: '',
    secondarySport: '',
    seasonPhase: 'offseason',
    experienceLevel: 'intermediate', // beginner, intermediate, advanced
    goals: [],
    
    // Equipment
    equipmentOwned: {},
    equipmentTier: 'starter', // starter, intermediate, elite
    hasGymAccess: false,
    hasFieldAccess: false,
    
    // Schedule
    practicesDaysPerWeek: 3,
    gameDaysPerWeek: 1,
    availableTrainingDays: ['monday', 'wednesday', 'friday'],
    preferredTrainingTime: 'afternoon',
    minutesPerSession: 45,
    
    // Nutrition
    allergies: [],
    dislikes: [],
    dietType: 'standard',
    mealsPerDay: 3,
    whoPreps: 'parent',
    cookingTime: 'moderate', // quick, moderate, detailed
    
    // Household / Living
    householdSize: 4,
    athletesInHousehold: 1,
    budgetLevel: 'standard', // budget, standard, premium
    livingSituation: '', // dorm, apartment, house, home (college)
  });
  
  // Dynamic steps based on athlete level
  const STEPS = formData.athleteLevel === 'college' ? COLLEGE_STEPS : BASE_STEPS;

  // Custom goals options based on athlete level
  const highSchoolGoals = [
    'Increase speed & agility',
    'Build strength & power',
    'Improve endurance',
    'Develop sport-specific skills',
    'Earn a college scholarship',
    'Make varsity team',
    'Get recruited by scouts',
    'Improve nutrition habits',
    'Better recovery & sleep',
    'Build mental toughness',
    'Increase vertical jump',
    'Improve 40-yard dash time',
  ];
  
  const collegeGoals = [
    'Improve performance metrics',
    'Build strength & power',
    'Increase speed & agility',
    'Recover faster between games',
    'Earn starting position',
    'Make All-Conference team',
    'Prepare for pro draft',
    'Build NIL brand & following',
    'Maintain academic eligibility',
    'Improve nutrition habits',
    'Develop leadership skills',
    'Stay healthy all season',
  ];
  
  const goalOptions = formData.athleteLevel === 'college' ? collegeGoals : highSchoolGoals;

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleEquipment = (itemId) => {
    setFormData(prev => ({
      ...prev,
      equipmentOwned: {
        ...prev.equipmentOwned,
        [itemId]: !prev.equipmentOwned[itemId]
      }
    }));
  };

  const toggleGoal = (goal) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.includes(goal)
        ? prev.goals.filter(g => g !== goal)
        : [...prev.goals, goal]
    }));
  };

  const toggleTrainingDay = (day) => {
    setFormData(prev => ({
      ...prev,
      availableTrainingDays: prev.availableTrainingDays.includes(day)
        ? prev.availableTrainingDays.filter(d => d !== day)
        : [...prev.availableTrainingDays, day]
    }));
  };

  const calculateEquipmentTier = () => {
    const owned = Object.keys(formData.equipmentOwned).filter(k => formData.equipmentOwned[k]);
    let starterCount = 0, intermediateCount = 0, eliteCount = 0;
    
    Object.values(EQUIPMENT_TIERS).forEach(category => {
      category.items.starter?.forEach(item => { if (owned.includes(item.id)) starterCount++; });
      category.items.intermediate?.forEach(item => { if (owned.includes(item.id)) intermediateCount++; });
      category.items.elite?.forEach(item => { if (owned.includes(item.id)) eliteCount++; });
    });
    
    if (eliteCount >= 2) return 'elite';
    if (intermediateCount >= 2) return 'intermediate';
    return 'starter';
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      // Auto-calculate equipment tier when leaving equipment step
      if (STEPS[currentStep].id === 'equipment') {
        updateFormData('equipmentTier', calculateEquipmentTier());
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('aura_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Save onboarding data
      await axios.post(`${API}/users/onboarding`, formData, { headers });
      
      // Update user context with onboarding_completed = true
      updateUser({ ...user, onboarding_completed: true });
      
      toast.success('Profile setup complete!');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to save onboarding:', error);
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const renderStep = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-aura-primary rounded-2xl flex items-center justify-center mx-auto">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-3xl text-white uppercase tracking-tight">
                Welcome to Aura Athlete Development
              </h2>
              <p className="text-aura-text-muted mt-2 max-w-md mx-auto">
                You now have a system designed to help you improve faster, stay consistent, and reach your full potential.
              </p>
              <p className="text-aura-primary font-heading font-bold text-lg mt-4 uppercase tracking-wider">
                Aura is earned, not given.
              </p>
            </div>
            <div className="bg-aura-surface rounded-xl p-6 text-left max-w-md mx-auto">
              <p className="text-sm text-aura-text-muted mb-4">We'll personalize your system:</p>
              <ul className="space-y-2">
                {['Your athlete profile', 'Sport & goals', 'Available equipment', 'Training schedule', 'Nutrition preferences'].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-white text-sm">
                    <Check className="w-4 h-4 text-aura-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'level':
        return (
          <div className="space-y-6 max-w-lg mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                What's Your Level?
              </h2>
              <p className="text-aura-text-muted mt-1">This helps us customize your experience</p>
            </div>
            
            <div className="space-y-4">
              {[
                { 
                  id: 'high_school', 
                  name: 'High School Athlete', 
                  desc: 'Training to earn a college scholarship and get recruited',
                  icon: GraduationCap,
                  color: 'bg-blue-500'
                },
                { 
                  id: 'college', 
                  name: 'College Athlete', 
                  desc: 'Competing at the collegiate level, building your NIL brand',
                  icon: Trophy,
                  color: 'bg-aura-primary'
                },
                { 
                  id: 'pro', 
                  name: 'Pro / Elite Athlete', 
                  desc: 'Professional or elite-level competitor',
                  icon: Star,
                  color: 'bg-yellow-500'
                },
              ].map(level => (
                <button
                  key={level.id}
                  onClick={() => updateFormData('athleteLevel', level.id)}
                  className={`w-full p-5 rounded-xl border-2 text-left transition-all ${
                    formData.athleteLevel === level.id
                      ? 'border-aura-secondary bg-aura-secondary/10'
                      : 'border-white/10 bg-aura-surface hover:border-white/20'
                  }`}
                  data-testid={`level-${level.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 ${level.color} rounded-xl flex items-center justify-center`}>
                      <level.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-heading font-bold text-white uppercase">{level.name}</p>
                      <p className="text-sm text-aura-text-muted">{level.desc}</p>
                    </div>
                    {formData.athleteLevel === level.id && (
                      <Check className="w-6 h-6 text-aura-secondary" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case 'college_info':
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                College Information
              </h2>
              <p className="text-aura-text-muted mt-1">Tell us about your collegiate career</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white">Your Name *</Label>
                <Input
                  value={formData.athleteName}
                  onChange={(e) => updateFormData('athleteName', e.target.value)}
                  placeholder="Enter your name"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                  data-testid="college-name-input"
                />
              </div>
              
              <div>
                <Label className="text-white">Current School *</Label>
                <Select value={formData.currentSchool} onValueChange={(v) => updateFormData('currentSchool', v)}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select your school" />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10 max-h-60">
                    {COLLEGES.map(school => (
                      <SelectItem key={school.id} value={school.id} className="text-white">
                        {school.name} {school.conference && `(${school.conference})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.currentSchool === 'other' && (
                <div>
                  <Label className="text-white">School Name *</Label>
                  <Input
                    value={formData.customSchoolName}
                    onChange={(e) => updateFormData('customSchoolName', e.target.value)}
                    placeholder="Enter your school name"
                    className="bg-aura-bg border-white/10 text-white mt-1"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Year *</Label>
                  <Select value={formData.collegeYear} onValueChange={(v) => updateFormData('collegeYear', v)}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {[
                        { id: 'freshman', name: 'Freshman' },
                        { id: 'redshirt_freshman', name: 'Redshirt Freshman' },
                        { id: 'sophomore', name: 'Sophomore' },
                        { id: 'redshirt_sophomore', name: 'Redshirt Sophomore' },
                        { id: 'junior', name: 'Junior' },
                        { id: 'redshirt_junior', name: 'Redshirt Junior' },
                        { id: 'senior', name: 'Senior' },
                        { id: '5th_year', name: '5th Year' },
                        { id: 'grad', name: 'Graduate Student' },
                      ].map(year => (
                        <SelectItem key={year.id} value={year.id} className="text-white">{year.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Division</Label>
                  <Select value={formData.conferenceLevel} onValueChange={(v) => updateFormData('conferenceLevel', v)}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {['D1', 'D2', 'D3', 'NAIA', 'JUCO'].map(div => (
                        <SelectItem key={div} value={div} className="text-white">{div}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-white">Scholarship Status</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { id: 'full', label: 'Full Ride' },
                    { id: 'partial', label: 'Partial' },
                    { id: 'walk_on', label: 'Walk-On' },
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => updateFormData('scholarshipType', type.id)}
                      className={`p-3 rounded-lg border text-sm transition-colors ${
                        formData.scholarshipType === type.id
                          ? 'bg-aura-primary border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Eligibility Years Remaining</Label>
                <Select value={formData.eligibilityYears?.toString()} onValueChange={(v) => updateFormData('eligibilityYears', parseInt(v))}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {[0, 1, 2, 3, 4].map(y => (
                      <SelectItem key={y} value={y.toString()} className="text-white">{y} year{y !== 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 'nil':
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                NIL Status
              </h2>
              <p className="text-aura-text-muted mt-1">Tell us about your Name, Image, Likeness activities</p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-aura-surface rounded-xl border border-white/10">
                <div>
                  <p className="font-medium text-white">Do you have any NIL deals?</p>
                  <p className="text-sm text-aura-text-muted">Endorsements, sponsorships, appearances, etc.</p>
                </div>
                <button
                  onClick={() => updateFormData('hasNilDeals', !formData.hasNilDeals)}
                  className={`w-14 h-8 rounded-full transition-colors ${
                    formData.hasNilDeals ? 'bg-aura-secondary' : 'bg-white/20'
                  }`}
                  data-testid="nil-deals-toggle"
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-transform ${
                    formData.hasNilDeals ? 'translate-x-7' : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {formData.hasNilDeals && (
                <div>
                  <Label className="text-white">Estimated Annual NIL Earnings</Label>
                  <Select value={formData.nilEarningsRange} onValueChange={(v) => updateFormData('nilEarningsRange', v)}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      <SelectItem value="under_10k" className="text-white">Under $10,000</SelectItem>
                      <SelectItem value="10k_50k" className="text-white">$10,000 - $50,000</SelectItem>
                      <SelectItem value="50k_100k" className="text-white">$50,000 - $100,000</SelectItem>
                      <SelectItem value="100k_500k" className="text-white">$100,000 - $500,000</SelectItem>
                      <SelectItem value="over_500k" className="text-white">Over $500,000</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-white mb-2 block">NIL Interests</Label>
                <p className="text-sm text-aura-text-muted mb-3">What types of NIL opportunities interest you?</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'social_media', label: 'Social Media' },
                    { id: 'endorsements', label: 'Brand Endorsements' },
                    { id: 'appearances', label: 'Appearances' },
                    { id: 'camps', label: 'Camps & Clinics' },
                    { id: 'merchandise', label: 'Merchandise' },
                    { id: 'autographs', label: 'Autographs' },
                  ].map(interest => (
                    <button
                      key={interest.id}
                      onClick={() => {
                        const updated = formData.nilInterests.includes(interest.id)
                          ? formData.nilInterests.filter(i => i !== interest.id)
                          : [...formData.nilInterests, interest.id];
                        updateFormData('nilInterests', updated);
                      }}
                      className={`px-4 py-2 rounded-full text-sm transition-colors ${
                        formData.nilInterests.includes(interest.id)
                          ? 'bg-aura-secondary text-white'
                          : 'bg-white/10 text-aura-text-muted hover:bg-white/20'
                      }`}
                    >
                      {interest.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Agent / Representation Status</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { id: 'none', label: 'No Agent' },
                    { id: 'seeking', label: 'Looking' },
                    { id: 'have_agent', label: 'Have Agent' },
                  ].map(status => (
                    <button
                      key={status.id}
                      onClick={() => updateFormData('agentStatus', status.id)}
                      className={`p-3 rounded-lg border text-sm transition-colors ${
                        formData.agentStatus === status.id
                          ? 'bg-aura-primary border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                      }`}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'athlete':
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                Athlete Information
              </h2>
              <p className="text-aura-text-muted mt-1">Tell us about the athlete</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white">Athlete's Name *</Label>
                <Input
                  value={formData.athleteName}
                  onChange={(e) => updateFormData('athleteName', e.target.value)}
                  placeholder="Enter athlete's name"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Age *</Label>
                  <Select value={formData.athleteAge} onValueChange={(v) => updateFormData('athleteAge', v)}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select age" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {Array.from({ length: 14 }, (_, i) => i + 8).map(age => (
                        <SelectItem key={age} value={age.toString()} className="text-white">{age} years old</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Grade</Label>
                  <Select value={formData.athleteGrade} onValueChange={(v) => updateFormData('athleteGrade', v)}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {['5th', '6th', '7th', '8th', '9th (Freshman)', '10th (Sophomore)', '11th (Junior)', '12th (Senior)'].map(grade => (
                        <SelectItem key={grade} value={grade} className="text-white">{grade}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-white">Your Name (Parent/Guardian)</Label>
                <Input
                  value={formData.parentName}
                  onChange={(e) => updateFormData('parentName', e.target.value)}
                  placeholder="Enter your name"
                  className="bg-aura-bg border-white/10 text-white mt-1"
                />
              </div>
              
              <div>
                <Label className="text-white">Your Role</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { id: 'parent', label: 'Parent' },
                    { id: 'coach', label: 'Coach' },
                    { id: 'guardian', label: 'Guardian' },
                  ].map(role => (
                    <button
                      key={role.id}
                      onClick={() => updateFormData('parentRole', role.id)}
                      className={`p-3 rounded-lg border text-sm transition-colors ${
                        formData.parentRole === role.id
                          ? 'bg-aura-primary border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                      }`}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'sport':
        return (
          <div className="space-y-6 max-w-lg mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                Sport & Goals
              </h2>
              <p className="text-aura-text-muted mt-1">What are we training for?</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white">Primary Sport *</Label>
                <Select value={formData.primarySport} onValueChange={(v) => updateFormData('primarySport', v)}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select sport" />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {SPORTS.map(sport => (
                      <SelectItem key={sport.id} value={sport.id} className="text-white">{sport.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.primarySport && SPORTS.find(s => s.id === formData.primarySport)?.positions.length > 0 && (
                <div>
                  <Label className="text-white">Position</Label>
                  <Select value={formData.position} onValueChange={(v) => updateFormData('position', v)}>
                    <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent className="bg-aura-surface border-white/10">
                      {SPORTS.find(s => s.id === formData.primarySport)?.positions.map(pos => (
                        <SelectItem key={pos} value={pos} className="text-white">{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-white">Current Season Phase</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {SEASON_PHASES.map(phase => (
                    <button
                      key={phase.id}
                      onClick={() => updateFormData('seasonPhase', phase.id)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.seasonPhase === phase.id
                          ? 'bg-aura-primary/20 border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                      }`}
                    >
                      <p className="font-medium text-sm">{phase.name}</p>
                      <p className="text-xs text-aura-text-muted mt-0.5">{phase.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Goals (Select all that apply)</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                  {goalOptions.map(goal => (
                    <button
                      key={goal}
                      onClick={() => toggleGoal(goal)}
                      className={`p-2 rounded-lg border text-left text-sm transition-colors ${
                        formData.goals.includes(goal)
                          ? 'bg-aura-primary/20 border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                      }`}
                    >
                      {goal}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'equipment':
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                Equipment Check
              </h2>
              <p className="text-aura-text-muted mt-1">Check what you own or have access to</p>
            </div>
            
            <div className="grid gap-4">
              {Object.entries(EQUIPMENT_TIERS).map(([catId, category]) => {
                const Icon = category.icon;
                return (
                  <Card key={catId} className="bg-aura-surface border-white/10">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading font-bold text-white uppercase tracking-tight flex items-center gap-2 text-base">
                        <Icon className="w-5 h-5 text-aura-primary" />
                        {category.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Starter */}
                      <div>
                        <Badge className="bg-green-500/20 text-green-400 border-0 text-xs mb-2">Starter Kit (Under $100)</Badge>
                        <div className="grid grid-cols-2 gap-2">
                          {category.items.starter?.map(item => (
                            <button
                              key={item.id}
                              onClick={() => toggleEquipment(item.id)}
                              className={`p-2 rounded-lg border text-left text-sm transition-colors ${
                                formData.equipmentOwned[item.id]
                                  ? 'bg-green-500/20 border-green-500 text-white'
                                  : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {formData.equipmentOwned[item.id] ? (
                                  <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                                ) : (
                                  <div className="w-4 h-4 border border-white/20 rounded flex-shrink-0" />
                                )}
                                <span>{item.name}</span>
                              </div>
                              {item.alt && (
                                <p className="text-xs text-aura-text-muted mt-1 ml-6">Alt: {item.alt}</p>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Intermediate */}
                      {category.items.intermediate?.length > 0 && (
                        <div>
                          <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs mb-2">Upgrade Kit ($100-250)</Badge>
                          <div className="grid grid-cols-2 gap-2">
                            {category.items.intermediate?.map(item => (
                              <button
                                key={item.id}
                                onClick={() => toggleEquipment(item.id)}
                                className={`p-2 rounded-lg border text-left text-sm transition-colors ${
                                  formData.equipmentOwned[item.id]
                                    ? 'bg-blue-500/20 border-blue-500 text-white'
                                    : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {formData.equipmentOwned[item.id] ? (
                                    <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                  ) : (
                                    <div className="w-4 h-4 border border-white/20 rounded flex-shrink-0" />
                                  )}
                                  <span>{item.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Elite */}
                      {category.items.elite?.length > 0 && (
                        <div>
                          <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs mb-2">Elite ($250+)</Badge>
                          <div className="grid grid-cols-2 gap-2">
                            {category.items.elite?.map(item => (
                              <button
                                key={item.id}
                                onClick={() => toggleEquipment(item.id)}
                                className={`p-2 rounded-lg border text-left text-sm transition-colors ${
                                  formData.equipmentOwned[item.id]
                                    ? 'bg-purple-500/20 border-purple-500 text-white'
                                    : 'bg-aura-bg border-white/10 text-aura-text-muted hover:border-white/20'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  {formData.equipmentOwned[item.id] ? (
                                    <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                                  ) : (
                                    <div className="w-4 h-4 border border-white/20 rounded flex-shrink-0" />
                                  )}
                                  <span>{item.name}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => updateFormData('hasGymAccess', !formData.hasGymAccess)}
                className={`p-4 rounded-lg border text-center transition-colors ${
                  formData.hasGymAccess
                    ? 'bg-aura-primary/20 border-aura-primary text-white'
                    : 'bg-aura-bg border-white/10 text-aura-text-muted'
                }`}
              >
                <Dumbbell className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm font-medium">Gym Access</p>
              </button>
              <button
                onClick={() => updateFormData('hasFieldAccess', !formData.hasFieldAccess)}
                className={`p-4 rounded-lg border text-center transition-colors ${
                  formData.hasFieldAccess
                    ? 'bg-aura-primary/20 border-aura-primary text-white'
                    : 'bg-aura-bg border-white/10 text-aura-text-muted'
                }`}
              >
                <Target className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm font-medium">Field/Track Access</p>
              </button>
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                Training Schedule
              </h2>
              <p className="text-aura-text-muted mt-1">When can you train?</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white">Available Training Days</Label>
                <div className="grid grid-cols-7 gap-1 mt-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => {
                    const dayId = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][idx];
                    return (
                      <button
                        key={dayId}
                        onClick={() => toggleTrainingDay(dayId)}
                        className={`p-2 rounded-lg border text-xs font-medium transition-colors ${
                          formData.availableTrainingDays.includes(dayId)
                            ? 'bg-aura-primary border-aura-primary text-white'
                            : 'bg-aura-bg border-white/10 text-aura-text-muted'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Practices Per Week</Label>
                <Select value={formData.practicesDaysPerWeek.toString()} onValueChange={(v) => updateFormData('practicesDaysPerWeek', parseInt(v))}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {[0, 1, 2, 3, 4, 5, 6].map(n => (
                      <SelectItem key={n} value={n.toString()} className="text-white">{n} days</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-white">Preferred Training Time</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { id: 'morning', label: 'Morning', icon: '🌅' },
                    { id: 'afternoon', label: 'Afternoon', icon: '☀️' },
                    { id: 'evening', label: 'Evening', icon: '🌙' },
                  ].map(time => (
                    <button
                      key={time.id}
                      onClick={() => updateFormData('preferredTrainingTime', time.id)}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        formData.preferredTrainingTime === time.id
                          ? 'bg-aura-primary border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted'
                      }`}
                    >
                      <span className="text-lg">{time.icon}</span>
                      <p className="text-xs mt-1">{time.label}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Minutes Per Training Session</Label>
                <Select value={formData.minutesPerSession.toString()} onValueChange={(v) => updateFormData('minutesPerSession', parseInt(v))}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {[20, 30, 45, 60, 75, 90].map(n => (
                      <SelectItem key={n} value={n.toString()} className="text-white">{n} minutes</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 'nutrition':
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                Nutrition Preferences
              </h2>
              <p className="text-aura-text-muted mt-1">Help us personalize meal plans</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white">Any Food Allergies?</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {['Dairy', 'Eggs', 'Peanuts', 'Tree Nuts', 'Fish', 'Shellfish', 'Wheat', 'Soy'].map(allergy => (
                    <button
                      key={allergy}
                      onClick={() => {
                        const a = allergy.toLowerCase();
                        updateFormData('allergies', 
                          formData.allergies.includes(a) 
                            ? formData.allergies.filter(x => x !== a)
                            : [...formData.allergies, a]
                        );
                      }}
                      className={`p-2 rounded-lg border text-xs text-center transition-colors ${
                        formData.allergies.includes(allergy.toLowerCase())
                          ? 'bg-red-500/20 border-red-500 text-red-400'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted'
                      }`}
                    >
                      {allergy}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Who Prepares Most Meals?</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { id: 'parent', label: 'Parent/Guardian' },
                    { id: 'athlete', label: 'Athlete' },
                    { id: 'both', label: 'Both' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => updateFormData('whoPreps', opt.id)}
                      className={`p-3 rounded-lg border text-sm transition-colors ${
                        formData.whoPreps === opt.id
                          ? 'bg-aura-primary border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label className="text-white">Cooking Time Preference</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { id: 'quick', label: 'Quick & Easy', desc: '10-15 min' },
                    { id: 'moderate', label: 'Moderate', desc: '20-30 min' },
                    { id: 'detailed', label: 'Detailed', desc: '45+ min' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => updateFormData('cookingTime', opt.id)}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        formData.cookingTime === opt.id
                          ? 'bg-aura-primary border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted'
                      }`}
                    >
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-aura-text-muted">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'household':
        return (
          <div className="space-y-6 max-w-md mx-auto">
            <div className="text-center">
              <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight">
                Household Details
              </h2>
              <p className="text-aura-text-muted mt-1">For meal planning and grocery lists</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-white">Household Size (eating meals together)</Label>
                <Select value={formData.householdSize.toString()} onValueChange={(v) => updateFormData('householdSize', parseInt(v))}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                      <SelectItem key={n} value={n.toString()} className="text-white">{n} people</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-white">Athletes in Household</Label>
                <Select value={formData.athletesInHousehold.toString()} onValueChange={(v) => updateFormData('athletesInHousehold', parseInt(v))}>
                  <SelectTrigger className="bg-aura-bg border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {[1, 2, 3, 4, 5].map(n => (
                      <SelectItem key={n} value={n.toString()} className="text-white">{n} athlete{n > 1 ? 's' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-white">Budget Level</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { id: 'budget', label: 'Budget', desc: 'Essential items' },
                    { id: 'standard', label: 'Standard', desc: 'Balanced options' },
                    { id: 'premium', label: 'Premium', desc: 'Top quality' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => updateFormData('budgetLevel', opt.id)}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        formData.budgetLevel === opt.id
                          ? 'bg-aura-primary border-aura-primary text-white'
                          : 'bg-aura-bg border-white/10 text-aura-text-muted'
                      }`}
                    >
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-xs text-aura-text-muted">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'complete':
        const tier = calculateEquipmentTier();
        const tierInfo = {
          starter: { color: 'text-green-400', bg: 'bg-green-500/20', label: 'Starter' },
          intermediate: { color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Intermediate' },
          elite: { color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Elite' },
        };
        
        return (
          <div className="text-center space-y-6 max-w-md mx-auto">
            <div className="w-20 h-20 bg-green-500 rounded-2xl flex items-center justify-center mx-auto">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-3xl text-white uppercase tracking-tight">
                Your System is Ready
              </h2>
              <p className="text-aura-text-muted mt-2">
                Here's your personalized development summary
              </p>
              <p className="text-aura-primary font-heading font-bold text-lg mt-4 uppercase tracking-wider">
                Aura is earned, not given.
              </p>
            </div>
            
            <Card className="bg-aura-surface border-white/10 text-left">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-aura-text-muted">Athlete</span>
                  <span className="text-white font-medium">{formData.athleteName || 'Not set'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-aura-text-muted">Sport</span>
                  <span className="text-white font-medium capitalize">{formData.primarySport || 'Not set'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-aura-text-muted">Equipment Tier</span>
                  <Badge className={`${tierInfo[tier].bg} ${tierInfo[tier].color} border-0`}>
                    {tierInfo[tier].label}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-aura-text-muted">Training Days</span>
                  <span className="text-white font-medium">{formData.availableTrainingDays.length} days/week</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-aura-text-muted">Season Phase</span>
                  <span className="text-white font-medium capitalize">{formData.seasonPhase.replace('-', ' ')}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-aura-text-muted">Goals</span>
                  <span className="text-white font-medium">{formData.goals.length} selected</span>
                </div>
              </CardContent>
            </Card>
            
            <p className="text-sm text-aura-text-muted">
              Your sessions will be tailored to your <span className={tierInfo[tier].color}>{tierInfo[tier].label}</span> equipment level
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-aura-bg">
      {/* Progress Header */}
      <header className="sticky top-0 z-40 bg-aura-bg/95 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-aura-text-muted uppercase tracking-wider">
              Step {currentStep + 1} of {STEPS.length}
            </span>
            <span className="text-xs text-aura-primary">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-1" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isComplete = idx < currentStep;
              const isCurrent = idx === currentStep;
              
              return (
                <div 
                  key={step.id}
                  className={`flex flex-col items-center ${
                    idx <= currentStep ? 'text-aura-primary' : 'text-aura-text-muted'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isComplete ? 'bg-aura-primary' : isCurrent ? 'bg-aura-primary/20 border border-aura-primary' : 'bg-white/5'
                  }`}>
                    {isComplete ? <Check className="w-4 h-4 text-white" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className="text-[10px] mt-1 hidden sm:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 py-8 max-w-2xl mx-auto">
        {renderStep()}
      </main>

      {/* Navigation Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-aura-bg/95 backdrop-blur-lg border-t border-white/5 p-4">
        <div className="max-w-2xl mx-auto flex justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="text-aura-text-muted"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
          
          {currentStep === STEPS.length - 1 ? (
            <Button
              onClick={handleComplete}
              disabled={saving}
              className="bg-aura-primary hover:bg-aura-primary-hover text-white"
            >
              {saving ? 'Saving...' : 'Complete Setup'}
              <Check className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-aura-primary hover:bg-aura-primary-hover text-white"
            >
              Continue
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
};

export default OnboardingPage;
